import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, PauseCircle, PlayCircle, XCircle, CheckCircle2, Users } from 'lucide-react';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { prospectDate } from '@/utils/prospectTime';

export const MOD_TABS = [
  { value: 'pending_review', label: 'A aguardar verificação', color: 'bg-blue-100 text-blue-800' },
  { value: 'moderation', label: 'Em pausa (moderação)', color: 'bg-orange-100 text-orange-800' },
  { value: 'suspicious', label: 'Suspeitas', color: 'bg-red-100 text-red-800' },
  { value: 'rejected', label: 'Rejeitadas', color: 'bg-rose-100 text-rose-800' },
];
const REASONS = [
  { value: 'fraud', label: 'Suspeita de burla' },
  { value: 'company_unverified', label: 'Dados da empresa por verificar' },
  { value: 'inappropriate', label: 'Conteúdo inadequado' },
  { value: 'other', label: 'Outro' },
];
const ACTION_LABEL = { pause: 'Pausar (moderação)', reactivate: 'Reactivar', reject: 'Rejeitar', approve: 'Aprovar',
  auto_pause: 'Pausa automática', warn_candidates: 'Candidatos avisados', pending_review: 'À espera de aprovação', flagged: 'Bandeira vermelha' };
const fmt = (v) => prospectDate(v)?.toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) || '—';

export const isModPaused = (job) => job.status === 'paused' && job.paused_reason === 'moderation';
export const modStatusLabel = (job) => (isModPaused(job) ? 'em pausa (moderação)' : job.status === 'pending_review' ? 'a aguardar verificação' : job.status === 'rejected' ? 'rejeitada' : ({ active: 'activa', draft: 'rascunho', expired: 'expirada', paused: 'em pausa', filled: 'preenchida', cancelled: 'cancelada' }[job.status] || job.status));

export const ModerationDialog = ({ job, action, onClose, onDone }) => {
  const [code, setCode] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const needsReason = action === 'pause' || action === 'reject';
  const valid = !needsReason || (code && (code !== 'other' || text.trim()));
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/jobs/${job.id}/moderate`, { action, reason_code: code || null, reason_text: text.trim() || null });
      toast.success(`${ACTION_LABEL[action]}: feito`);
      onDone(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="moderation-dialog">
      <div className="bg-white rounded-xl max-w-md w-full p-5 space-y-3">
        <h3 className="font-bold text-lg">{ACTION_LABEL[action]} — {job.title_pt}</h3>
        {needsReason || action === 'reactivate' || action === 'approve' ? (
          <>
            <label className="text-sm font-medium block">Motivo{needsReason ? ' (obrigatório)' : ' (opcional)'}
              <select value={code} onChange={(e) => setCode(e.target.value)} className="mt-1 w-full h-10 rounded-md border border-gray-300 px-2 text-sm" data-testid="moderation-reason-select">
                <option value="">— escolher —</option>
                {REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </label>
            <textarea value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={500} placeholder={code === 'other' ? 'Descreva o motivo (obrigatório)' : 'Detalhes (opcional)'}
              className="w-full rounded-md border border-gray-300 p-2 text-sm" data-testid="moderation-reason-text" />
          </>
        ) : null}
        {action === 'reject' && <p className="text-xs text-slate-600" data-testid="moderation-refund-note">O crédito usado na publicação é devolvido automaticamente à empresa (o trial ilimitado não desconta créditos).</p>}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="moderation-cancel">Cancelar</Button>
          <Button onClick={submit} disabled={busy || !valid} className={needsReason ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'} data-testid="moderation-confirm">{ACTION_LABEL[action]}</Button>
        </div>
      </div>
    </div>
  );
};

const WarnCandidates = ({ job }) => {
  const [busy, setBusy] = useState(false);
  const [warned, setWarned] = useState(job.candidates_warned_at);
  if (!(isModPaused(job) || job.status === 'rejected')) return null;
  const send = async () => {
    if (!window.confirm(`Enviar um aviso de segurança (suspeita de burla) a ${job.applications_count} candidato(s) desta vaga?`)) return;
    setBusy(true);
    try { const { data } = await api.post(`/admin/jobs/${job.id}/warn-candidates`); setWarned(data.warned_at); (data.sent < data.total ? toast.error : toast.success)(`${data.sent} de ${data.total} candidato(s) avisado(s)`); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); } finally { setBusy(false); }
  };
  return (
    <span className="ml-2 inline-flex items-center gap-2">
      <Button size="sm" variant="outline" disabled={busy} onClick={send} className="h-7 text-xs text-red-700 border-red-200" data-testid={`job-warn-candidates-${job.id}`}>{warned ? 'Reenviar aviso aos candidatos' : 'Avisar candidatos por email'}</Button>
      {warned && <span className="text-emerald-700" data-testid={`job-warned-at-${job.id}`}>Avisados a {fmt(warned)}</span>}
    </span>
  );
};

const Applicants = ({ job }) => {
  const [data, setData] = useState(null);
  if (!job.applications_count) return null;
  return (
    <div className="mt-2 text-xs" data-testid={`job-applicants-${job.id}`}>
      <button type="button" className="inline-flex items-center gap-1 text-blue-700 underline" onClick={() => (data ? setData(null) : api.get(`/admin/jobs/${job.id}/applicants`).then((r) => setData(r.data)))} data-testid={`job-applicants-toggle-${job.id}`}>
        <Users className="h-3 w-3" />{job.applications_count} candidatura(s) — {data ? 'esconder' : 'ver candidatos para os avisar'}
      </button>
      <WarnCandidates job={job} />
      {data && <ul className="mt-1 space-y-0.5 pl-4 list-disc text-gray-700">{data.items.map((a) => <li key={a.id}>{a.candidate_name} · <a className="text-blue-700" href={`mailto:${a.candidate_email}`}>{a.candidate_email}</a> · {fmt(a.applied_at)}</li>)}</ul>}
    </div>
  );
};

const History = ({ job }) => {
  const [open, setOpen] = useState(false);
  const h = job.moderation_history || [];
  if (!h.length) return null;
  return (
    <div className="mt-1 text-xs" data-testid={`job-mod-history-${job.id}`}>
      <button type="button" className="text-gray-500 underline" onClick={() => setOpen(!open)} data-testid={`job-mod-history-toggle-${job.id}`}>Histórico de moderação ({h.length})</button>
      {open && <ol className="mt-1 border-l-2 border-gray-200 pl-3 space-y-0.5">{[...h].reverse().map((x, i) => <li key={i} className="text-gray-600"><strong>{ACTION_LABEL[x.action] || x.action}</strong> · {x.by} · {fmt(x.at)}{x.reason ? ` — ${x.reason}` : ''}</li>)}</ol>}
    </div>
  );
};

export const ModerationInfo = ({ job }) => (
  <>
    {(job.moderation_flags || []).length > 0 && (
      <div className="mt-2 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-800" data-testid={`job-flags-${job.id}`}>
        <span className="font-bold inline-flex items-center gap-1"><AlertTriangle className="h-3 w-3" />Sinais suspeitos:</span>{' '}
        {job.moderation_flags.map((f) => `${f.strength === 'strong' ? '🔴' : '🟠'} ${f.label}`).join(' · ')}
      </div>
    )}
    {job.reports_count > 0 && <p className="mt-1 text-xs font-semibold text-red-700" data-testid={`job-reports-${job.id}`}>⚠️ {job.reports_count} denúncia(s) de candidatos</p>}
    {job.credit_refund?.type && <p className="mt-1 text-xs font-semibold text-emerald-700" data-testid={`job-refunded-${job.id}`}>↩ Crédito devolvido à empresa ({job.credit_refund.type === 'free' ? 'vaga grátis' : 'crédito de pack'})</p>}
    <Applicants job={job} />
    <History job={job} />
  </>
);

export const ModerationButtons = ({ job, onAction }) => {
  const b = (action, icon, cls) => (
    <Button key={action} variant="outline" size="sm" className={cls} onClick={() => onAction(job, action)} data-testid={`admin-job-${action}-${job.id}`}>{icon}{ACTION_LABEL[action]}</Button>
  );
  const out = [];
  if (job.status === 'pending_review') out.push(b('approve', <CheckCircle2 className="h-3.5 w-3.5 mr-1" />, 'text-emerald-700 border-emerald-200'));
  if (job.status === 'paused' || job.status === 'rejected') out.push(b('reactivate', <PlayCircle className="h-3.5 w-3.5 mr-1" />, 'text-emerald-700 border-emerald-200'));
  if (job.status !== 'paused' && job.status !== 'rejected') out.push(b('pause', <PauseCircle className="h-3.5 w-3.5 mr-1" />, 'text-orange-700 border-orange-200'));
  if (job.status !== 'rejected') out.push(b('reject', <XCircle className="h-3.5 w-3.5 mr-1" />, 'text-red-700 border-red-200'));
  return out;
};

// Abre o diálogo a partir dos botões do email (?moderate=<id>&action=approve|reject)
export const useModerationDeepLink = (items, open) => {
  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const id = q.get('moderate');
    const job = id && items.find((j) => j.id === id);
    if (job) {
      open(job, q.get('action') === 'reject' ? 'reject' : 'approve');
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, [items, open]);
};
